/**
 * Channel Partner detail — Admin Portal command centre (tabbed).
 * Overview, compliance (KYB), attributed users, fees, portal team, operations mirror.
 *
 * @author MyMoolah Treasury Platform
 * @date 2026-04-07 (rev 2026-07-14: Phase C tabbed command centre)
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { fieldInputClass, validateEmail } from '../../utils/contactFieldValidation';
import { RepWelcomeEmailSection } from './RepWelcomeEmailSection';
import {
  PartnerDetailTabNav,
  parsePartnerDetailTab,
  type PartnerDetailTab,
} from './channel-partner-detail/PartnerDetailTabNav';
import { OperationsTab } from './channel-partner-detail/OperationsTab';
import { PartnerAgreementsStatusCard } from './channel-partner-detail/PartnerAgreementsStatusCard';
import { CpaAgreementSetupCard } from './channel-partner-detail/CpaAgreementSetupCard';
import { PartnerCredentialsCard } from './channel-partner-detail/PartnerCredentialsCard';

const API = axios.create({ baseURL: '/api/v1' });
API.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

interface DisbursementClient {
  id: number;
  client_code: string;
  company_name: string;
  entity_type: string;
  registration_number: string | null;
  contact_name: string | null;
  contact_email: string;
  contact_phone: string | null;
  status: string;
  kyb_status: string;
  onboarding_status: string | null;
  partner_type: string | null;
  ledger_account_code: string | null;
  deposit_reference: string | null;
  float_limit: string | null;
  white_label_slug: string | null;
  is_test_client?: boolean;
  // Revenue-share settlement (CPRSA v1.1.0) — bank account number itself is
  // never returned by the API; only last4 for masked display.
  revenue_share_enabled?: boolean;
  bank_account_holder?: string | null;
  bank_name?: string | null;
  bank_branch_code?: string | null;
  bank_account_number_last4?: string | null;
  is_vat_vendor?: boolean;
  vat_number?: string | null;
  /** Form-only transient: new account number entered in the edit form. */
  bank_account_number?: string;
  created_at: string;
  updated_at: string;
}

interface KybDocument {
  id: number;
  document_type: string;
  entity_type: string;
  status: string;
  verification_score: number | null;
  validation_result: { errors?: string[]; warnings?: string[]; score?: number } | null;
  content_type: string | null;
  file_size_bytes: number | null;
  verified_at: string | null;
  verified_by: string | null;
  rejection_reason: string | null;
  created_at: string;
}

interface RequiredDoc {
  documentType: string;
  description: string;
  required: boolean;
}

interface ClientFee {
  id: number;
  rail: string;
  fee_type: string;
  flat_fee_cents: number;
  percentage_fee: string;
  min_fee_cents: number;
  max_fee_cents: number | null;
  vat_inclusive: boolean;
  effective_from: string;
  effective_to: string | null;
  created_at: string;
}

interface ClientUser {
  id: number;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

interface AttributedWalletUser {
  id: number;
  userRef: string;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  email: string | null;
  kycStatus: string | null;
  partnerAttributedAt: string | null;
  wallet: { balance: number | string; status: string } | null;
  transactionSummary: { count: number; totalAmount: number };
}

interface PartnerSummary {
  userStats: {
    total_users: number;
    kyc_verified: number;
    kyc_pending: number;
    active_wallets: number;
    registrations_7d: number;
    registrations_30d: number;
  };
  performance: {
    last7d: { transactionCount: number; transactionValue: number };
    last30d: { transactionCount: number; transactionValue: number };
  };
}

/* ---------- Status badge color maps ---------- */

const CLIENT_STATUS_BADGE: Record<string, string> = {
  pending:   'bg-amber-50 text-amber-700',
  active:    'bg-emerald-50 text-emerald-700',
  suspended: 'bg-red-50 text-red-700',
  closed:    'bg-gray-100 text-gray-500',
};

const KYB_STATUS_BADGE: Record<string, string> = {
  none:         'bg-gray-100 text-gray-600',
  not_started:  'bg-gray-100 text-gray-600',
  submitted:    'bg-blue-50 text-blue-700',
  under_review: 'bg-indigo-50 text-indigo-700',
  verified:     'bg-emerald-50 text-emerald-700',
  approved:     'bg-emerald-50 text-emerald-700',
  rejected:     'bg-red-50 text-red-700',
};

const DOC_STATUS_BADGE: Record<string, string> = {
  pending:           'bg-amber-50 text-amber-700',
  processing:        'bg-blue-50 text-blue-700',
  extracted:         'bg-blue-50 text-blue-700',
  validated:         'bg-teal-50 text-teal-700',
  validation_failed: 'bg-red-50 text-red-700',
  verified:          'bg-emerald-50 text-emerald-700',
  approved:          'bg-emerald-50 text-emerald-700',
  rejected:          'bg-red-50 text-red-700',
  expired:           'bg-gray-100 text-gray-500',
};

/* Statuses for which a maker may still verify/reject the document. */
const REVIEWABLE_DOC_STATUSES = ['pending', 'processing', 'extracted', 'validated', 'validation_failed'];

/* ---------- Display helpers ---------- */

// Only the entity types supported by the KYB verify path.
const ENTITY_TYPE_LABELS: Record<string, string> = {
  company: 'Company',
  sole_proprietor: 'Sole Proprietor',
  trust: 'Trust',
};

// Canonical KYB document types (kybComplianceService extraction set).
const DOC_TYPE_LABELS: Record<string, string> = {
  cor15: 'CIPC Registration',
  cor14_3: 'CoR14.3 (Directors)',
  ck1: 'CK1 (Founding Statement)',
  trust_deed: 'Trust Deed',
  id_document: 'ID Document',
  proof_of_address: 'Proof of Address',
  bank_confirmation: 'Bank Confirmation',
  tax_clearance: 'Tax clearance (SARS TCS or VAT103)',
  resolution: 'Resolution / Letter of Authority',
};

const PARTNER_TYPE_LABELS: Record<string, string> = {
  disbursement: 'Corporate disbursement',
  channel_partner: 'Channel partner',
  hybrid: 'Hybrid (channel partner + disbursement)',
  client: 'Client',
};

const formatEntityType = (et: string) => ENTITY_TYPE_LABELS[et] || et.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const formatDocType = (dt: string) => DOC_TYPE_LABELS[dt] || dt.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const DOC_OK_STATUSES = new Set(['validated', 'verified', 'approved']);
const DOC_CLOSED_STATUSES = new Set(['rejected', 'validation_failed', 'expired']);

/** Latest current upload per type — hides duplicate Tax Clearance (and similar) rows. */
function latestDocumentsByType(documents: KybDocument[]): Array<KybDocument & { earlierCount: number }> {
  const byType = new Map<string, KybDocument[]>();
  for (const doc of documents) {
    const list = byType.get(doc.document_type) || [];
    list.push(doc);
    byType.set(doc.document_type, list);
  }
  const out: Array<KybDocument & { earlierCount: number }> = [];
  for (const list of byType.values()) {
    const sorted = [...list].sort((a, b) => {
      const aOk = DOC_OK_STATUSES.has(a.status) ? 1 : 0;
      const bOk = DOC_OK_STATUSES.has(b.status) ? 1 : 0;
      if (bOk !== aOk) return bOk - aOk;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    out.push({ ...sorted[0], earlierCount: list.length - 1 });
  }
  return out.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}

function hasCurrentUpload(documents: KybDocument[], documentType: string): boolean {
  return documents.some((d) => d.document_type === documentType && !DOC_CLOSED_STATUSES.has(d.status));
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const fmtDateOnly = (iso: string) => {
  const datePart = iso.includes('T') ? iso.split('T')[0] : iso;
  return new Date(`${datePart}T12:00:00`).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtZar = (amount: number | string | null | undefined) => {
  const n = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  if (amount == null || Number.isNaN(n)) {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(0);
  }
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(n);
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const tomorrowIso = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

const feeDatePart = (iso: string) => (iso.includes('T') ? iso.split('T')[0] : iso);

const isFeeActive = (fee: ClientFee, today: string) =>
  feeDatePart(fee.effective_from) <= today
  && (!fee.effective_to || feeDatePart(fee.effective_to) >= today);

const isFeeScheduled = (fee: ClientFee, today: string) => feeDatePart(fee.effective_from) > today;

const isFeeHistorical = (fee: ClientFee, today: string) =>
  !isFeeActive(fee, today) && !isFeeScheduled(fee, today);

const fmtCents = (cents: number) => `R ${(cents / 100).toFixed(2)}`;
const fmtPct = (pct: string) => `${(parseFloat(pct) * 100).toFixed(2)}%`;
const fmtBytes = (b: number | null) => (b == null ? '\u2014' : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

// Canonical fee rails (must match config/disbursementFeeRails.js).
const CUSTOM_RAIL_VALUE = 'custom';

const RAIL_OPTIONS = [
  { value: 'eft', label: 'EFT' },
  { value: 'payshap', label: 'PayShap' },
  { value: 'rtp', label: 'RTP' },
  { value: 'qr', label: 'QR' },
  { value: 'ep_topup', label: 'EP-Topup' },
  { value: 'ott_cashout', label: 'OTT-Cashout' },
  { value: 'sbsa_cashout', label: 'SBSA-Cashout' },
  { value: 'cbr', label: 'CBR' },
  { value: CUSTOM_RAIL_VALUE, label: 'Custom…' },
] as const;

const STANDARD_RAIL_VALUES = RAIL_OPTIONS
  .filter((r) => r.value !== CUSTOM_RAIL_VALUE)
  .map((r) => r.value);

const RAIL_LABELS: Record<string, string> = Object.fromEntries(
  RAIL_OPTIONS.filter((r) => r.value !== CUSTOM_RAIL_VALUE).map((r) => [r.value, r.label]),
);

const normalizeCustomRail = (input: string) =>
  input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);

const formatRailLabel = (rail: string) => {
  if (RAIL_LABELS[rail]) return RAIL_LABELS[rail];
  return rail
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const FEE_TYPE_OPTIONS = [
  { value: 'flat', label: 'Flat' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'flat_plus_percentage', label: 'Flat + Percentage' },
] as const;

export const DisbursementClientDetailOverlay: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parsePartnerDetailTab(searchParams.get('tab'));

  const setActiveTab = (tab: PartnerDetailTab) => {
    setSearchParams({ tab }, { replace: true });
  };

  const [client, setClient] = useState<DisbursementClient | null>(null);
  const [fees, setFees] = useState<ClientFee[]>([]);
  const [documents, setDocuments] = useState<KybDocument[]>([]);
  const [requiredDocs, setRequiredDocs] = useState<RequiredDoc[]>([]);
  const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  /* Edit mode state */
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<Partial<DisbursementClient>>({});
  const [saving, setSaving] = useState(false);
  const [editEmailError, setEditEmailError] = useState<string | null>(null);

  /* KYB document upload state */
  const [showUpload, setShowUpload] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* Fee form state */
  const [showFeeForm, setShowFeeForm] = useState(false);
  const [feeForm, setFeeForm] = useState({
    rail: 'eft',
    custom_rail: '',
    fee_type: 'flat',
    flat_fee_cents: '',
    percentage_fee: '',
    min_fee_cents: '',
    max_fee_cents: '',
    effective_from: todayIso(),
    vat_inclusive: true,
  });
  const [addingFee, setAddingFee] = useState(false);
  const [feeAdjustMode, setFeeAdjustMode] = useState(false);

  /* Client users state */
  const [showUserForm, setShowUserForm] = useState(false);
  const [userForm, setUserForm] = useState({ email: '', name: '', role: 'viewer', password: '' });
  const [userEmailError, setUserEmailError] = useState<string | null>(null);
  const [addingUser, setAddingUser] = useState(false);

  /* Attributed wallet users */
  const [attributedUsers, setAttributedUsers] = useState<AttributedWalletUser[]>([]);
  const [attributedLoading, setAttributedLoading] = useState(false);

  /* Channel partner fleet summary (overview KPIs) */
  const [partnerSummary, setPartnerSummary] = useState<PartnerSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  /* Action messages */
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  /* ---------- Data fetching ---------- */

  const fetchClient = useCallback(async () => {
    try {
      const res = await API.get(`/disbursement-clients/${clientId}`);
      const data = res.data.data;
      const { fees: clientFees, users, ...clientData } = data;
      setClient(clientData as DisbursementClient);
      setFees(clientFees || []);
      setClientUsers(users || []);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else {
        setError(err?.response?.data?.error || 'Failed to load client');
      }
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const fetchKybDocuments = useCallback(async () => {
    try {
      const res = await API.get(`/disbursement-clients/${clientId}/kyb-documents`);
      setDocuments(res.data.data.documents || []);
      setRequiredDocs(res.data.data.requiredDocuments || []);
    } catch {
      /* non-fatal — keep the rest of the page usable */
    }
  }, [clientId]);

  const fetchFees = useCallback(async () => {
    try {
      const res = await API.get(`/disbursement-clients/${clientId}/fees`);
      setFees(res.data.data.fees || []);
    } catch {
      /* keep current fees from getClient */
    }
  }, [clientId]);

  const fetchAttributedUsers = useCallback(async () => {
    setAttributedLoading(true);
    try {
      const res = await API.get(`/disbursement-clients/${clientId}/attributed-users`, {
        params: { limit: 50, page: 1 },
      });
      setAttributedUsers(res.data.data.users || []);
    } catch {
      setAttributedUsers([]);
    } finally {
      setAttributedLoading(false);
    }
  }, [clientId]);

  const isChannelPartnerProfile = (pt: string | null | undefined) =>
    pt === 'channel_partner' || pt === 'hybrid';

  const fetchPartnerSummary = useCallback(async () => {
    if (!clientId) return;
    setSummaryLoading(true);
    try {
      const res = await API.get(`/admin/channel-partners/${clientId}/summary`);
      const data = res.data.data;
      setPartnerSummary({
        userStats: data.userStats,
        performance: {
          last7d: data.performance?.last7d || { transactionCount: 0, transactionValue: 0 },
          last30d: data.performance?.last30d || { transactionCount: 0, transactionValue: 0 },
        },
      });
    } catch {
      setPartnerSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchClient();
    fetchKybDocuments();
    fetchFees();
  }, [fetchClient, fetchKybDocuments, fetchFees]);

  useEffect(() => {
    if (!client) return;
    const cpOnly: PartnerDetailTab[] = ['users', 'operations'];
    if (!isChannelPartnerProfile(client.partner_type) && cpOnly.includes(activeTab)) {
      setSearchParams({ tab: 'overview' }, { replace: true });
    }
  }, [client, activeTab, setSearchParams]);

  useEffect(() => {
    if (client && isChannelPartnerProfile(client.partner_type) && activeTab === 'overview') {
      fetchPartnerSummary();
    }
  }, [client?.partner_type, client?.id, activeTab, fetchPartnerSummary]);

  useEffect(() => {
    if (client && isChannelPartnerProfile(client.partner_type) && activeTab === 'users') {
      fetchAttributedUsers();
    }
  }, [client?.partner_type, client?.id, activeTab, fetchAttributedUsers]);

  /* ---------- Client edit ---------- */

  const startEdit = () => {
    if (!client) return;
    setEditForm({
      company_name: client.company_name,
      contact_name: client.contact_name || '',
      contact_email: client.contact_email,
      contact_phone: client.contact_phone || '',
      status: client.status,
      float_limit: client.float_limit || '',
      white_label_slug: client.white_label_slug || '',
      partner_type: client.partner_type || 'disbursement',
      bank_account_holder: client.bank_account_holder || '',
      bank_name: client.bank_name || '',
      bank_branch_code: client.bank_branch_code || '',
      bank_account_number: '', // blank = keep existing; enter digits to replace
      is_vat_vendor: !!client.is_vat_vendor,
      vat_number: client.vat_number || '',
    });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditForm({});
  };

  const saveEdit = async () => {
    const emailError = validateEmail(editForm.contact_email || '');
    if (emailError) {
      setEditEmailError(emailError);
      setActionMsg({ type: 'error', text: emailError });
      return;
    }
    setEditEmailError(null);
    setSaving(true);
    setActionMsg(null);
    try {
      const payload = { ...editForm };
      // Bank account number: only send when a replacement was typed — a blank
      // field must not wipe the stored (encrypted) number.
      if (!String(payload.bank_account_number || '').trim()) {
        delete payload.bank_account_number;
      }
      await API.patch(`/disbursement-clients/${clientId}`, payload);
      setActionMsg({ type: 'success', text: 'Client updated successfully.' });
      setEditMode(false);
      await fetchClient();
    } catch (err: any) {
      setActionMsg({ type: 'error', text: err?.response?.data?.error || 'Failed to update client' });
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Archive / Unarchive (soft lifecycle) ---------- */

  const handleArchive = async () => {
    const reason = window.prompt(
      `Archive "${client.company_name}"?\n\nThis revokes the partner's API credentials and blocks portal login. All financial, audit, and agreement records are retained (banking-grade). The partner can be unarchived later.\n\nReason (optional):`,
      '',
    );
    if (reason === null) return; // user cancelled
    setActionMsg(null);
    try {
      await API.post(`/disbursement-clients/${clientId}/archive`, { reason: reason || undefined });
      setActionMsg({ type: 'success', text: 'Client archived. Credentials revoked.' });
      await fetchClient();
    } catch (err: any) {
      setActionMsg({ type: 'error', text: err?.response?.data?.error || 'Failed to archive client' });
    }
  };

  const handleUnarchive = async () => {
    if (!window.confirm(`Unarchive "${client.company_name}"?\n\nThis returns the partner to "suspended" status. API credentials stay revoked and must be re-issued via the Credentials Center. Re-activation still requires the full maker-checker KYB flow.`)) return;
    setActionMsg(null);
    try {
      await API.post(`/disbursement-clients/${clientId}/unarchive`, {});
      setActionMsg({ type: 'success', text: 'Client unarchived (status: suspended). Re-issue credentials to restore API access.' });
      await fetchClient();
    } catch (err: any) {
      setActionMsg({ type: 'error', text: err?.response?.data?.error || 'Failed to unarchive client' });
    }
  };

  /* ---------- KYB document actions ---------- */

  const reviewDocument = async (doc: KybDocument, action: 'verified' | 'rejected') => {
    setActionMsg(null);
    // 2026-10-13: verifying a document that failed AI validation requires an
    // explicit reason (audited) — parity with the pack-level maker override.
    const failedValidation =
      doc.status === 'validation_failed' ||
      (doc.validation_result?.errors?.length ?? 0) > 0;
    let reason: string | undefined;
    if (action === 'verified' && failedValidation) {
      const input = window.prompt(
        'This document failed AI validation. Record a reason documenting why the issues are acceptable (required, stored on the audit trail):'
      );
      if (input === null) return; // cancelled
      if (!input.trim()) {
        setActionMsg({ type: 'error', text: 'A reason is required to verify a document that failed validation.' });
        return;
      }
      reason = input.trim();
    } else if (action === 'rejected') {
      const input = window.prompt('Reason for rejection (stored on the document):');
      if (input === null) return; // cancelled
      reason = input.trim() || undefined;
    }
    try {
      await API.patch(`/disbursement-clients/${clientId}/kyb-documents/${doc.id}`, {
        status: action,
        ...(action === 'verified' ? { reason } : { rejection_reason: reason }),
      });
      setActionMsg({ type: 'success', text: `Document ${action}.` });
      await Promise.all([fetchKybDocuments(), fetchClient()]);
    } catch (err: any) {
      setActionMsg({ type: 'error', text: err?.response?.data?.error || 'Review failed' });
    }
  };

  const viewDocument = async (docId: number) => {
    setActionMsg(null);
    try {
      const res = await API.get(`/disbursement-clients/${clientId}/kyb-documents/${docId}/review-url`);
      const url = res?.data?.data?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      else setActionMsg({ type: 'error', text: 'Could not open document' });
    } catch (err: any) {
      setActionMsg({ type: 'error', text: err?.response?.data?.error || 'Could not open document' });
    }
  };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
  };

  const uploadDocument = async () => {
    if (!uploadDocType || !uploadFile) return;
    setUploading(true);
    setActionMsg(null);
    try {
      const fd = new FormData();
      fd.append('document', uploadFile);
      fd.append('document_type', uploadDocType);
      await API.post(`/disbursement-clients/${clientId}/kyb-documents`, fd);
      setActionMsg({ type: 'success', text: 'Document uploaded — AI extraction running in the background.' });
      setShowUpload(false);
      setUploadDocType('');
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await fetchKybDocuments();
    } catch (err: any) {
      setActionMsg({ type: 'error', text: err?.response?.data?.error || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  /* ---------- Fee actions ---------- */

  const resetFeeForm = () => {
    setFeeForm({
      rail: 'eft',
      custom_rail: '',
      fee_type: 'flat',
      flat_fee_cents: '',
      percentage_fee: '',
      min_fee_cents: '',
      max_fee_cents: '',
      effective_from: todayIso(),
      vat_inclusive: true,
    });
    setFeeAdjustMode(false);
  };

  const resolveFeeFormRail = () => {
    if (feeForm.rail === CUSTOM_RAIL_VALUE) return normalizeCustomRail(feeForm.custom_rail);
    return feeForm.rail;
  };

  const openFeeAdjust = (fee: ClientFee) => {
    const isStandard = STANDARD_RAIL_VALUES.includes(fee.rail);
    setFeeForm({
      rail: isStandard ? fee.rail : CUSTOM_RAIL_VALUE,
      custom_rail: isStandard ? '' : fee.rail,
      fee_type: fee.fee_type,
      flat_fee_cents: String(fee.flat_fee_cents),
      percentage_fee: fee.percentage_fee,
      min_fee_cents: String(fee.min_fee_cents),
      max_fee_cents: fee.max_fee_cents != null ? String(fee.max_fee_cents) : '',
      effective_from: tomorrowIso(),
      vat_inclusive: fee.vat_inclusive !== false,
    });
    setFeeAdjustMode(true);
    setShowFeeForm(true);
    setActionMsg(null);
  };

  const addFee = async () => {
    if (feeForm.rail === CUSTOM_RAIL_VALUE && !feeForm.custom_rail.trim()) {
      setActionMsg({ type: 'error', text: 'Enter a name for the custom fee rail.' });
      return;
    }
    const rail = resolveFeeFormRail();
    if (!rail) {
      setActionMsg({ type: 'error', text: 'Custom rail name must contain letters or numbers.' });
      return;
    }

    setAddingFee(true);
    setActionMsg(null);
    try {
      await API.post(`/disbursement-clients/${clientId}/fees`, {
        rail: feeForm.rail === CUSTOM_RAIL_VALUE ? 'custom' : feeForm.rail,
        custom_rail: feeForm.rail === CUSTOM_RAIL_VALUE ? feeForm.custom_rail : undefined,
        fee_type: feeForm.fee_type,
        flat_fee_cents: feeForm.flat_fee_cents ? parseInt(feeForm.flat_fee_cents, 10) : 0,
        percentage_fee: feeForm.percentage_fee ? parseFloat(feeForm.percentage_fee) : 0,
        min_fee_cents: feeForm.min_fee_cents ? parseInt(feeForm.min_fee_cents, 10) : 0,
        max_fee_cents: feeForm.max_fee_cents ? parseInt(feeForm.max_fee_cents, 10) : null,
        effective_from: feeForm.effective_from,
        vat_inclusive: feeForm.vat_inclusive,
      });
      const isFuture = feeForm.effective_from > todayIso();
      setActionMsg({
        type: 'success',
        text: isFuture
          ? `Fee change scheduled from ${fmtDateOnly(feeForm.effective_from)}.`
          : 'Fee added successfully.',
      });
      setShowFeeForm(false);
      resetFeeForm();
      await fetchFees();
    } catch (err: any) {
      setActionMsg({ type: 'error', text: err?.response?.data?.error || 'Failed to add fee' });
    } finally {
      setAddingFee(false);
    }
  };

  /* ---------- Client user actions ---------- */

  const addClientUser = async () => {
    const emailError = validateEmail(userForm.email);
    if (emailError) {
      setUserEmailError(emailError);
      setActionMsg({ type: 'error', text: emailError });
      return;
    }
    setUserEmailError(null);
    setAddingUser(true);
    setActionMsg(null);
    try {
      await API.post(`/disbursement-clients/${clientId}/users`, userForm);
      setActionMsg({ type: 'success', text: 'User added successfully.' });
      setShowUserForm(false);
      setUserForm({ email: '', name: '', role: 'viewer', password: '' });
      await fetchClient();
    } catch (err: any) {
      setActionMsg({ type: 'error', text: err?.response?.data?.error || 'Failed to add user' });
    } finally {
      setAddingUser(false);
    }
  };

  const toggleUserActive = async (userId: number, currentActive: boolean) => {
    setActionMsg(null);
    try {
      await API.patch(`/disbursement-clients/${clientId}/users/${userId}`, { is_active: !currentActive });
      await fetchClient();
    } catch (err: any) {
      setActionMsg({ type: 'error', text: err?.response?.data?.error || 'Failed to update user status' });
    }
  };

  /* ---------- Rendering ---------- */

  if (loading) return <div className="p-8 text-center text-gray-400">Loading...</div>;
  if (notFound) {
    return (
      <div className="mymoolah-card p-8 text-center">
        <p className="admin-text-heading text-lg mb-4">Channel partner not found</p>
        <button onClick={() => navigate('/admin/channel-partners')}
          className="px-4 py-2 rounded-xl text-sm font-semibold bg-[var(--primary)] text-[var(--primary-foreground)]">
          Back to Channel Partners
        </button>
      </div>
    );
  }
  if (error) return <div className="p-6 text-red-600 bg-red-50 rounded-xl">{error}</div>;
  if (!client) return null;

  const today = todayIso();
  const activeFees = fees.filter((f) => isFeeActive(f, today));
  const scheduledFees = fees.filter((f) => isFeeScheduled(f, today));
  const historicalFees = fees.filter((f) => isFeeHistorical(f, today));

  const renderFeeRow = (fee: ClientFee, opts?: { dimmed?: boolean; showAdjust?: boolean }) => (
    <tr key={fee.id} className={opts?.dimmed ? 'opacity-50' : 'hover:bg-gray-50'}>
      <td className="px-4 py-2.5 font-medium text-gray-800">{formatRailLabel(fee.rail)}</td>
      <td className="px-4 py-2.5 text-gray-600 capitalize">{fee.fee_type.replace(/_/g, ' ')}</td>
      <td className="px-4 py-2.5 font-mono text-gray-700">{fmtCents(fee.flat_fee_cents)}</td>
      <td className="px-4 py-2.5 font-mono text-gray-700">{fmtPct(fee.percentage_fee)}</td>
      <td className="px-4 py-2.5 font-mono text-gray-700">{fmtCents(fee.min_fee_cents)}</td>
      <td className="px-4 py-2.5 font-mono text-gray-700">{fee.max_fee_cents != null ? fmtCents(fee.max_fee_cents) : '\u2014'}</td>
      <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmtDateOnly(fee.effective_from)}</td>
      <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">
        {fee.effective_to ? fmtDateOnly(fee.effective_to) : '\u2014'}
      </td>
      <td className="px-4 py-2.5 text-xs text-gray-600">{fee.vat_inclusive === false ? 'Excl' : 'Incl'}</td>
      {opts?.showAdjust && (
        <td className="px-4 py-2.5 text-right">
          <button
            type="button"
            onClick={() => openFeeAdjust(fee)}
            className="text-xs font-semibold text-[var(--primary)] hover:underline"
          >
            Schedule change
          </button>
        </td>
      )}
    </tr>
  );

  // Upload dropdown: FICA matrix only (no unrelated document types).

  return (
    <div className="space-y-5">
      {/* Action message banner */}
      {actionMsg && (
        <div className={`rounded-xl p-4 text-sm font-medium ${actionMsg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {actionMsg.text}
        </div>
      )}

      {client.status !== 'active' && client.onboarding_status !== 'activated' && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold">Partner not live yet</p>
          <p className="mt-1 text-amber-800">
            Verifying KYB documents here marks documents as verified only. To activate the Client Portal and provision float + deposit reference, continue in{' '}
            <strong>Partner Onboarding</strong> (screening → maker review → checker approval).
          </p>
          <button
            type="button"
            onClick={() => navigate(`/admin/partners?clientId=${client.id}`)}
            className="mt-3 inline-flex items-center rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-semibold text-[var(--primary-foreground)]"
          >
            Continue activation in Partner Onboarding →
          </button>
        </div>
      )}

      {/* Partner header + tab navigation */}
      <div className="mymoolah-card sticky top-0 z-10 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/channel-partners')}
            className="self-start rounded-lg p-2 text-sm text-gray-500 hover:bg-gray-100"
          >
            &larr; Back to Channel Partners
          </button>
          {isChannelPartnerProfile(client.partner_type) && (
            <button
              type="button"
              onClick={() => navigate(`/admin/channel-partners/${client.id}/portal/dashboard`)}
              className="self-start rounded-lg border border-[var(--border)] px-3 py-2 text-sm font-medium text-[var(--foreground)] hover:bg-gray-50"
            >
              Open portal view
            </button>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="admin-text-heading text-xl">{client.company_name}</h2>
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 font-mono text-xs font-medium text-gray-600">
                {client.client_code}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${CLIENT_STATUS_BADGE[client.status] || 'bg-gray-200 text-gray-600'}`}>
                {client.status}
              </span>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${KYB_STATUS_BADGE[client.kyb_status] || ''}`}>
                KYB: {(client.kyb_status || '').replace(/_/g, ' ') || '\u2014'}
              </span>
              {client.onboarding_status && (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium capitalize text-gray-600">
                  Onboarding: {client.onboarding_status.replace(/_/g, ' ')}
                </span>
              )}
            </div>
          </div>
          {activeTab === 'overview' && !editMode && (
            <div className="flex flex-wrap items-center gap-2">
              {['suspended', 'closed'].includes(client.status) && (
                <button
                  type="button"
                  onClick={handleArchive}
                  className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-100"
                >
                  Archive
                </button>
              )}
              {client.status === 'archived' && (
                <button
                  type="button"
                  onClick={handleUnarchive}
                  className="rounded-xl border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-800 hover:bg-blue-100"
                >
                  Unarchive
                </button>
              )}
              <button
                type="button"
                onClick={startEdit}
                className="rounded-xl bg-[var(--primary)] px-4 py-2 text-sm font-semibold text-[var(--primary-foreground)]"
              >
                Edit
              </button>
            </div>
          )}
        </div>
        <div className="mt-4">
          <PartnerDetailTabNav
            activeTab={activeTab}
            onTabChange={setActiveTab}
            showChannelPartnerTabs={isChannelPartnerProfile(client.partner_type)}
          />
        </div>
      </div>

      {/* ============ Tab: Overview ============ */}
      {activeTab === 'overview' && (
        <>
          {isChannelPartnerProfile(client.partner_type) && (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {summaryLoading ? (
                <div className="col-span-full text-sm text-gray-400">Loading partner metrics...</div>
              ) : partnerSummary ? (
                <>
                  <div className="mymoolah-card p-4">
                    <p className="text-xs font-semibold uppercase text-gray-400">Attributed users</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900">{partnerSummary.userStats.total_users}</p>
                    <p className="mt-0.5 text-xs text-gray-500">{partnerSummary.userStats.kyc_verified} KYC verified</p>
                  </div>
                  <div className="mymoolah-card p-4">
                    <p className="text-xs font-semibold uppercase text-gray-400">Active wallets</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900">{partnerSummary.userStats.active_wallets}</p>
                    <p className="mt-0.5 text-xs text-gray-500">{partnerSummary.userStats.kyc_pending} KYC pending</p>
                  </div>
                  <div className="mymoolah-card p-4">
                    <p className="text-xs font-semibold uppercase text-gray-400">New (7d)</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900">{partnerSummary.userStats.registrations_7d}</p>
                    <p className="mt-0.5 text-xs text-gray-500">{partnerSummary.userStats.registrations_30d} in 30d</p>
                  </div>
                  <div className="mymoolah-card p-4">
                    <p className="text-xs font-semibold uppercase text-gray-400">Txns (7d)</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900">{partnerSummary.performance.last7d.transactionCount}</p>
                    <p className="mt-0.5 text-xs text-gray-500">{fmtZar(partnerSummary.performance.last7d.transactionValue)} volume</p>
                  </div>
                </>
              ) : null}
            </div>
          )}

          <div className="mymoolah-card p-6">
        {editMode ? (
          /* Inline edit form (entity type / registration number are not editable
             post-create — they drive the KYB document matrix). */
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Company Name</label>
                <input type="text" value={editForm.company_name || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, company_name: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Partner Type</label>
                <select value={editForm.partner_type || 'disbursement'}
                  onChange={(e) => setEditForm((p) => ({ ...p, partner_type: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300">
                  {Object.entries(PARTNER_TYPE_LABELS).map(([val, lbl]) => (
                    <option key={val} value={val}>{lbl}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Contact Name</label>
                <input type="text" value={editForm.contact_name || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, contact_name: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Contact Email</label>
                <input
                  type="text"
                  inputMode="email"
                  autoComplete="email"
                  value={editForm.contact_email || ''}
                  onChange={(e) => {
                    setEditForm((p) => ({ ...p, contact_email: e.target.value }));
                    if (editEmailError) setEditEmailError(null);
                  }}
                  onBlur={(e) => setEditEmailError(validateEmail(e.target.value))}
                  className={fieldInputClass(!!editEmailError, 'w-full border rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2')}
                />
                {editEmailError && <p className="mt-1 text-xs text-red-600">{editEmailError}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Contact Phone</label>
                <input type="text" value={editForm.contact_phone || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, contact_phone: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Status</label>
                <select value={editForm.status || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300">
                  {Object.keys(CLIENT_STATUS_BADGE).map((s) => (
                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">White-label Slug</label>
                <input type="text" value={editForm.white_label_slug || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, white_label_slug: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Float Limit (ZAR)</label>
                <input type="text" value={editForm.float_limit || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, float_limit: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300" />
              </div>
            </div>

            {/* Revenue-share settlement (CPRSA v1.1.0) — nominated bank for
                excess payouts + VAT vendor status. Only relevant when the
                partner is on a revenue-share agreement. */}
            {client.revenue_share_enabled && (
              <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Revenue-share settlement</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Account Holder</label>
                    <input type="text" value={editForm.bank_account_holder || ''}
                      onChange={(e) => setEditForm((p) => ({ ...p, bank_account_holder: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Bank</label>
                    <input type="text" value={editForm.bank_name || ''}
                      onChange={(e) => setEditForm((p) => ({ ...p, bank_name: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Branch Code</label>
                    <input type="text" value={editForm.bank_branch_code || ''}
                      onChange={(e) => setEditForm((p) => ({ ...p, bank_branch_code: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Account Number</label>
                    <input
                      type="password"
                      inputMode="numeric"
                      autoComplete="off"
                      value={editForm.bank_account_number || ''}
                      placeholder={client.bank_account_number_last4 ? `···· ${client.bank_account_number_last4} (leave blank to keep)` : '7–16 digits'}
                      onChange={(e) => setEditForm((p) => ({ ...p, bank_account_number: e.target.value.replace(/\D/g, '').slice(0, 16) }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300" />
                    <p className="mt-1 text-[11px] text-gray-400">Stored encrypted; only the last 4 digits are ever displayed.</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">VAT Number</label>
                    <input type="text" value={editForm.vat_number || ''}
                      onChange={(e) => setEditForm((p) => ({ ...p, vat_number: e.target.value }))}
                      placeholder="4xxxxxxxxx"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300" />
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={!!editForm.is_vat_vendor}
                        onChange={(e) => setEditForm((p) => ({ ...p, is_vat_vendor: e.target.checked }))}
                        className="rounded border-gray-300" />
                      Registered VAT vendor (invoice shows 15% output VAT)
                    </label>
                  </div>
                </div>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={saveEdit} disabled={saving}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 bg-[var(--primary)] text-[var(--primary-foreground)]">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button onClick={cancelEdit}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold border border-gray-300 text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          /* Read-only fields */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-3">
            {[
              { label: 'Entity Type', value: formatEntityType(client.entity_type) },
              { label: 'Partner Type', value: client.partner_type ? (PARTNER_TYPE_LABELS[client.partner_type] || client.partner_type) : '\u2014' },
              { label: 'Registration Number', value: client.registration_number || '\u2014' },
              { label: 'Contact Name', value: client.contact_name || '\u2014' },
              { label: 'Contact Email', value: client.contact_email },
              { label: 'Contact Phone', value: client.contact_phone || '\u2014' },
              { label: 'Ledger Account', value: client.ledger_account_code || '\u2014' },
              { label: 'Deposit Reference', value: client.deposit_reference || '\u2014' },
              { label: 'White-label Slug', value: client.white_label_slug || '\u2014' },
              { label: 'Float Limit', value: client.float_limit ? `R ${parseFloat(client.float_limit).toFixed(2)}` : '\u2014' },
              ...(client.revenue_share_enabled ? [
                { label: 'Settlement Bank', value: client.bank_name ? `${client.bank_name} ····${client.bank_account_number_last4 || ''}` : '\u2014' },
                { label: 'Account Holder', value: client.bank_account_holder || '\u2014' },
                { label: 'Branch Code', value: client.bank_branch_code || '\u2014' },
                { label: 'VAT Vendor', value: client.is_vat_vendor ? `Yes${client.vat_number ? ` — ${client.vat_number}` : ''}` : 'No' },
              ] : []),
            ].map((f) => (
              <div key={f.label} className="flex items-baseline gap-2 py-1.5 border-b border-gray-50">
                <span className="text-xs font-semibold text-gray-400 uppercase w-40 shrink-0">{f.label}</span>
                <span className="admin-text-body text-sm text-gray-800">{f.value}</span>
              </div>
            ))}
            <div className="flex items-baseline gap-2 py-1.5 border-b border-gray-50">
              <span className="text-xs font-semibold text-gray-400 uppercase w-40 shrink-0">Created</span>
              <span className="admin-text-body text-sm text-gray-800">{fmtDate(client.created_at)}</span>
            </div>
          </div>
        )}
          </div>
        </>
      )}

      {/* ============ Tab: Compliance (KYB) ============ */}
      {activeTab === 'compliance' && (
        <>
      <CpaAgreementSetupCard clientId={client.id} />
      <PartnerAgreementsStatusCard clientId={client.id} />
      <PartnerCredentialsCard clientId={client.id} />
      <div className="mymoolah-card overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="admin-text-heading text-lg">KYB Documents</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              FICA minimum documents for {formatEntityType(client.entity_type)}. Upload clear JPG, PNG, or text-based PDF.
            </p>
          </div>
          <button onClick={() => setShowUpload(!showUpload)}
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-[var(--primary)] text-[var(--primary-foreground)]">
            {showUpload ? 'Cancel' : '+ Upload Document'}
          </button>
        </div>

        {/* Required-documents checklist */}
        {requiredDocs.length > 0 && (
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Required document checklist</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {requiredDocs.map((rd) => {
                const uploaded = documents.find((d) => d.document_type === rd.documentType);
                const ok = uploaded && ['validated', 'verified', 'approved'].includes(uploaded.status);
                return (
                  <div key={rd.documentType} className="flex items-center gap-2 text-sm">
                    <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${ok ? 'bg-emerald-500' : uploaded ? 'bg-amber-400' : 'bg-gray-300'}`} />
                    <span className="text-gray-700">{rd.description || formatDocType(rd.documentType)}</span>
                    {!rd.required && <span className="text-[10px] text-gray-400">(optional)</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {showUpload && (
          <div className="p-5 bg-gray-50 border-b border-gray-100">
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Document Type</label>
                <select value={uploadDocType} onChange={(e) => setUploadDocType(e.target.value)}
                  className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300">
                  <option value="">Select type...</option>
                  {requiredDocs
                    .filter((rd) => !hasCurrentUpload(documents, rd.documentType))
                    .map((rd) => (
                    <option key={rd.documentType} value={rd.documentType}>
                      {rd.description || formatDocType(rd.documentType)}{rd.required ? '' : ' (optional)'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">File (PDF / JPG / PNG)</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  onChange={onFilePicked}
                  className="block text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-[var(--primary)] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-[var(--primary-foreground)]"
                />
              </div>
              <button onClick={uploadDocument} disabled={uploading || !uploadDocType || !uploadFile}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 bg-[var(--primary)] text-[var(--primary-foreground)]">
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-left">
                {['Type', 'Status', 'Score', 'Size', 'Uploaded', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {documents.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                    No KYB documents uploaded yet
                  </td>
                </tr>
              ) : latestDocumentsByType(documents).map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50 align-top">
                  <td className="px-4 py-2.5 font-medium text-gray-800">
                    {formatDocType(doc.document_type)}
                    {doc.earlierCount > 0 && (
                      <p className="text-[11px] text-gray-400 mt-0.5 font-normal">
                        {doc.earlierCount} earlier upload{doc.earlierCount === 1 ? '' : 's'} of this type hidden
                      </p>
                    )}
                    {doc.validation_result?.errors && doc.validation_result.errors.length > 0 && (
                      <p className="text-[11px] text-red-600 mt-0.5 font-normal">{doc.validation_result.errors[0]}</p>
                    )}
                    {doc.rejection_reason && (
                      <p className="text-[11px] text-red-600 mt-0.5 font-normal">{doc.rejection_reason}</p>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${DOC_STATUS_BADGE[doc.status] || 'bg-gray-100 text-gray-600'}`}>
                      {doc.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-600">{doc.verification_score != null ? `${doc.verification_score}` : '\u2014'}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{fmtBytes(doc.file_size_bytes)}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{fmtDate(doc.created_at)}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-2 items-center">
                      <button onClick={() => viewDocument(doc.id)} type="button"
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50">
                        View
                      </button>
                      {REVIEWABLE_DOC_STATUSES.includes(doc.status) && (
                        <>
                          <button onClick={() => reviewDocument(doc, 'verified')}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[var(--success-color)]">
                            Verify
                          </button>
                          <button onClick={() => reviewDocument(doc, 'rejected')}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-300 text-red-600 hover:bg-red-50">
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
        </>
      )}

      {/* ============ Tab: Fees & rails ============ */}
      {activeTab === 'fees' && (
      <div className="mymoolah-card overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="admin-text-heading text-lg">Fee Configuration</h3>
          </div>
          <button onClick={() => {
            if (showFeeForm) {
              setShowFeeForm(false);
              resetFeeForm();
            } else {
              resetFeeForm();
              setShowFeeForm(true);
            }
          }}
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-[var(--primary)] text-[var(--primary-foreground)]">
            {showFeeForm ? 'Cancel' : '+ Add Fee'}
          </button>
        </div>

        {showFeeForm && (
          <div className="p-5 bg-gray-50 border-b border-gray-100">
            {feeAdjustMode && (
              <p className="text-sm text-gray-600 mb-3">
                Schedule a new fee for <span className="font-semibold">{formatRailLabel(resolveFeeFormRail() || feeForm.rail)}</span>.
                The current rate stays in effect until the day before the effective date.
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Effective From</label>
                <input
                  type="date"
                  value={feeForm.effective_from}
                  min={todayIso()}
                  onChange={(e) => setFeeForm((p) => ({ ...p, effective_from: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Rail</label>
                <select value={feeForm.rail} disabled={feeAdjustMode}
                  onChange={(e) => setFeeForm((p) => ({ ...p, rail: e.target.value, custom_rail: e.target.value === CUSTOM_RAIL_VALUE ? p.custom_rail : '' }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300 disabled:bg-gray-100 disabled:text-gray-500">
                  {RAIL_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              {feeForm.rail === CUSTOM_RAIL_VALUE && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Custom Rail Name</label>
                  <input
                    type="text"
                    value={feeForm.custom_rail}
                    disabled={feeAdjustMode}
                    onChange={(e) => setFeeForm((p) => ({ ...p, custom_rail: e.target.value }))}
                    placeholder="e.g. partner_xyz_fee"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Stored as a slug (letters, numbers, underscores). Shown as {formatRailLabel(normalizeCustomRail(feeForm.custom_rail) || 'your_rail')}.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Fee Type</label>
                <select value={feeForm.fee_type} onChange={(e) => setFeeForm((p) => ({ ...p, fee_type: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300">
                  {FEE_TYPE_OPTIONS.map((ft) => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Flat Fee (cents)</label>
                <input type="number" value={feeForm.flat_fee_cents}
                  onChange={(e) => setFeeForm((p) => ({ ...p, flat_fee_cents: e.target.value }))}
                  placeholder="0"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Percentage Fee (decimal)</label>
                <input type="number" step="0.0001" value={feeForm.percentage_fee}
                  onChange={(e) => setFeeForm((p) => ({ ...p, percentage_fee: e.target.value }))}
                  placeholder="0.0000"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Min Fee (cents)</label>
                <input type="number" value={feeForm.min_fee_cents}
                  onChange={(e) => setFeeForm((p) => ({ ...p, min_fee_cents: e.target.value }))}
                  placeholder="0"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Max Fee (cents, optional)</label>
                <input type="number" value={feeForm.max_fee_cents}
                  onChange={(e) => setFeeForm((p) => ({ ...p, max_fee_cents: e.target.value }))}
                  placeholder="optional"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300" />
              </div>
              <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-3">
                <input
                  id="fee-vat-inclusive"
                  type="checkbox"
                  checked={feeForm.vat_inclusive}
                  onChange={(e) => setFeeForm((p) => ({ ...p, vat_inclusive: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-[var(--primary)] focus:ring-green-300"
                />
                <label htmlFor="fee-vat-inclusive" className="text-sm text-gray-700">
                  Fees are <span className="font-semibold">VAT inclusive</span> (15% VAT included in amounts)
                </label>
              </div>
            </div>
            <div className="mt-4">
              <button onClick={addFee} disabled={addingFee}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 bg-[var(--primary)] text-[var(--primary-foreground)]">
                {addingFee
                  ? 'Saving...'
                  : feeForm.effective_from > todayIso() || feeAdjustMode
                    ? 'Schedule Fee Change'
                    : 'Add Fee'}
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-left">
                {['Rail', 'Fee Type', 'Flat Fee', 'Percentage', 'Min Fee', 'Max Fee', 'Effective From', 'Effective To', 'VAT', ''].map((h) => (
                  <th key={h || 'actions'} className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {activeFees.length === 0 && scheduledFees.length === 0 && historicalFees.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-400">
                    No fees configured
                  </td>
                </tr>
              ) : (
                <>
                  {activeFees.map((fee) => renderFeeRow(fee, { showAdjust: true }))}
                  {scheduledFees.length > 0 && (
                    <>
                      <tr>
                        <td colSpan={10} className="px-4 py-2 text-xs font-semibold text-amber-700 uppercase bg-amber-50">
                          Scheduled Changes
                        </td>
                      </tr>
                      {scheduledFees.map((fee) => renderFeeRow(fee))}
                    </>
                  )}
                  {historicalFees.length > 0 && (
                    <>
                      <tr>
                        <td colSpan={10} className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase bg-gray-50">
                          Historical Fees
                        </td>
                      </tr>
                      {historicalFees.map((fee) => renderFeeRow(fee, { dimmed: true }))}
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* ============ Tab: Portal & team ============ */}
      {activeTab === 'portal' && (
        <div className="space-y-5">
          {isChannelPartnerProfile(client.partner_type) && (
            <RepWelcomeEmailSection
              clientId={client.id}
              contactEmail={client.contact_email}
              onMessage={(msg) => setActionMsg(msg)}
            />
          )}

      <div className="mymoolah-card overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="admin-text-heading text-lg">Client Portal Users</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Manage portal login users for this channel partner
            </p>
          </div>
          <button onClick={() => setShowUserForm(!showUserForm)}
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-[var(--primary)] text-[var(--primary-foreground)]">
            {showUserForm ? 'Cancel' : '+ Add User'}
          </button>
        </div>

        {showUserForm && (
          <div className="p-5 bg-gray-50 border-b border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Email</label>
                <input
                  type="text"
                  inputMode="email"
                  autoComplete="email"
                  value={userForm.email}
                  onChange={(e) => {
                    setUserForm((p) => ({ ...p, email: e.target.value }));
                    if (userEmailError) setUserEmailError(null);
                  }}
                  onBlur={(e) => setUserEmailError(validateEmail(e.target.value))}
                  placeholder="user@company.com"
                  className={fieldInputClass(!!userEmailError, 'w-full border rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2')}
                />
                {userEmailError && <p className="mt-1 text-xs text-red-600">{userEmailError}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Name</label>
                <input type="text" value={userForm.name}
                  onChange={(e) => setUserForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Full name"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Role</label>
                <select value={userForm.role}
                  onChange={(e) => setUserForm((p) => ({ ...p, role: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300">
                  <option value="admin">Admin</option>
                  <option value="maker">Maker</option>
                  <option value="checker">Checker</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Password</label>
                <input type="password" value={userForm.password}
                  onChange={(e) => setUserForm((p) => ({ ...p, password: e.target.value }))}
                  placeholder="Initial password"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300" />
              </div>
            </div>
            <div className="mt-4">
              <button onClick={addClientUser} disabled={addingUser || !userForm.email || !userForm.name || !userForm.password}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 bg-[var(--primary)] text-[var(--primary-foreground)]">
                {addingUser ? 'Adding...' : 'Add User'}
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-left">
                {['Name', 'Email', 'Role', 'Status', 'Last Login', 'Created'].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {clientUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                    No client users yet
                  </td>
                </tr>
              ) : clientUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-800">{user.name}</td>
                  <td className="px-4 py-2.5 text-gray-600 text-xs">{user.email}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium uppercase ${
                      user.role === 'admin' ? 'bg-blue-50 text-blue-700' :
                      user.role === 'maker' ? 'bg-emerald-50 text-emerald-700' :
                      user.role === 'checker' ? 'bg-amber-50 text-amber-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <button
                      onClick={() => toggleUserActive(user.id, user.is_active)}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                        user.is_active
                          ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                          : 'bg-red-50 text-red-700 hover:bg-red-100'
                      }`}
                    >
                      {user.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{user.last_login_at ? fmtDate(user.last_login_at) : '\u2014'}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{fmtDate(user.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
        </div>
      )}

      {/* ============ Tab: Attributed users ============ */}
      {activeTab === 'users' && isChannelPartnerProfile(client.partner_type) && (
        <div className="mymoolah-card overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="admin-text-heading text-lg">Attributed wallet users</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                Wallet users who registered via this partner&apos;s registration link
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate(`/admin/channel-partners/${client.id}/portal/users`)}
                className="text-sm font-semibold text-[var(--primary)] hover:underline"
              >
                Full list &amp; export
              </button>
              <button
                type="button"
                onClick={() => navigate(`/admin/users?disbursementClientId=${client.id}`)}
                className="text-sm font-semibold text-gray-600 hover:underline"
              >
                User Management
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-left">
                  {['Name', 'Phone', 'KYC', 'Balance', 'Attributed', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {attributedLoading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400">Loading...</td>
                  </tr>
                ) : attributedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                      No attributed wallet users yet
                    </td>
                  </tr>
                ) : (
                  attributedUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-800">
                        {[u.firstName, u.lastName].filter(Boolean).join(' ') || '\u2014'}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 text-xs">{u.phoneNumber || '\u2014'}</td>
                      <td className="px-4 py-2.5 text-gray-600 capitalize">{u.kycStatus || 'pending'}</td>
                      <td className="px-4 py-2.5 text-gray-700">{fmtZar(u.wallet?.balance)}</td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">
                        {u.partnerAttributedAt ? fmtDate(u.partnerAttributedAt) : '\u2014'}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/users?highlightUserId=${u.id}`)}
                            className="text-xs font-semibold text-[var(--primary)] hover:underline"
                          >
                            View user
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/transactions?userId=${u.id}`)}
                            className="text-xs font-semibold text-gray-600 hover:underline"
                          >
                            Transactions
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {partnerSummary && (
            <p className="border-t border-gray-100 px-5 py-3 text-xs text-gray-500">
              Showing first 50 of {partnerSummary.userStats.total_users} attributed users.
              Use <strong>Full list &amp; export</strong> for the complete set.
            </p>
          )}
        </div>
      )}

      {/* ============ Tab: Operations ============ */}
      {activeTab === 'operations' && isChannelPartnerProfile(client.partner_type) && (
        <OperationsTab
          clientId={client.id}
          companyName={client.company_name}
          whiteLabelSlug={client.white_label_slug}
        />
      )}
    </div>
  );
};

/** @deprecated alias — use DisbursementClientDetailOverlay */
export const ChannelPartnerDetailOverlay = DisbursementClientDetailOverlay;
